import React, { useState, useEffect } from 'react';
import EditableSection from 'components/v2/EditableSection';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import { titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import ROLES from 'constants/UserRoles';
import SessionStore from 'stores/SessionStore';
import { checkPermissonForEditShipperBillingModule } from './utils';

const BillingInfoSummary = ({ fields }) => {
  const { t } = useTranslation();
  const getFormattedAddress = (data = {}) => {
    let address = '';
    if (data.city) {
      address += data.city + ',';
    }
    if (data.state) {
      address += ' ' + data.state + ' ';
    }
    if (data.zipcode) {
      address += ' ' + data.zipcode;
    }
    return address;
  };
  return (
    <div className="grid grid-cols-2 gap-4">
      <DescriptionListItem label={t('NAME')} className="col-span-full">
        {fields.name}
      </DescriptionListItem>
      <DescriptionListItem label={t('EMAIL')} className="col-span-full">
        {fields.email}
      </DescriptionListItem>
      <DescriptionListItem label={t('ADDRESS')} className="col-span-full">
        {fields.street && <p>{fields.street}</p>}
        {getFormattedAddress(fields)}
      </DescriptionListItem>
    </div>
  );
};

const BillingInfoForm = ({
  fields,
  onChange,
  saving,
  onSaveShipper,
  onSaveSuccess,
  onSaveFail,
}) => {
  const { t } = useTranslation();
  const { name, email, street, city, state, zipcode } = fields;

  const saveForm = async () => {
    try {
      await onSaveShipper();
      onSaveSuccess(fields);
    } catch (e) {
      onSaveFail();
    }
  };

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="col-span-full">
        <Label>{t('NAME')}</Label>
        <TextField
          className="mt-1"
          value={name}
          onChange={(val) => onChange('name', val)}
        />
      </div>
      <div className="col-span-full">
        <Label>{t('EMAIL')}</Label>
        <TextField
          type="email"
          value={email}
          onChange={(val) => onChange('email', val)}
          className="mt-1"
        />
      </div>
      <div className="col-span-full">
        <Label>{t('STREET_ADDRESS_1')}</Label>
        <TextField
          type="text"
          value={street}
          className="mt-1"
          onChange={(val) => onChange('street', val)}
        />
      </div>
      <div className="">
        <Label>{titleize(t('CITY'))}</Label>
        <TextField
          type="text"
          value={city}
          className="mt-1"
          onChange={(val) => onChange('city', val)}
        />
      </div>
      <div className="">
        <Label>{titleize(t('STATE'))}</Label>
        <TextField
          type="text"
          value={state}
          className="mt-1"
          onChange={(val) => onChange('state', val)}
        />
      </div>
      <div className="">
        <Label>{t('ZIP')}</Label>
        <TextField
          type="number"
          value={zipcode}
          className="mt-1"
          onChange={(val) => onChange('zipcode', val)}
        />
      </div>
    </div>
  );
};

const BillingInfo = ({ data, className, onUpdateShipper }) => {
  const { t } = useTranslation();
  const [initialData, setinitialData] = useState(data);
  const [billingInfoData, setBillingInfoData] = useState(data);
  const getInitData = () => {
    if (Object.keys(data)) {
      setBillingInfoData(data);
      setinitialData(data);
    }
  };
  const checkPermissonForEditBillingAddress = () => {
    const logedInUser = SessionStore.getUser();
    /**
     * if user type is Shipper or Admin with role finance-admin or role shipper-onboarding.
     * He can edit billing address else not.
     */
    if (
      logedInUser._type === ROLES.SHIPPER ||
      logedInUser.roles.includes(ROLES.FINANCE_ADMIN) ||
      logedInUser.roles.includes(ROLES.SHIPPER_ONBOARDING)
    ) {
      return true;
    }
    return false;
  };
  const handleSave = async () => {
    if (
      !Object.keys(billingInfoData).every(
        (key) => initialData[key] === billingInfoData[key],
      )
    ) {
      await onUpdateShipper({
        billing_address: {
          ...billingInfoData,
          isDisabled: checkPermissonForEditBillingAddress(),
        },
      });
    }
  };

  const handleAferSave = (onSaveSuccess) => {
    return (updatedData = {}) => {
      setinitialData((prev) => ({ ...prev, ...updatedData }));
      onSaveSuccess();
    };
  };

  useEffect(() => {
    getInitData();
  }, [data]);
  const onChange = (field, value) => {
    setBillingInfoData((prev) => ({ ...prev, [field]: value }));
  };
  return (
    <EditableSection
      className={className}
      title={t('BILLING_INFORMATION')}
      canEdit={checkPermissonForEditShipperBillingModule()}
    >
      {(editMode, saving, onSaveSuccess, onSaveFail) =>
        editMode ? (
          <BillingInfoForm
            fields={billingInfoData}
            onChange={onChange}
            saving={saving}
            onSaveSuccess={handleAferSave(onSaveSuccess)}
            onSaveFail={onSaveFail}
            onSaveShipper={() => handleSave()}
          />
        ) : (
          <BillingInfoSummary fields={initialData} />
        )
      }
    </EditableSection>
  );
};

export default BillingInfo;
